export interface CourseDateRange {
  startDate: string
  endDate: string
}

export interface CourseDateRangeRow extends CourseDateRange {
  id: string
}

export function toISODateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function todayISODate(): string {
  return toISODateString(new Date())
}

export function formatDateRangeLabel(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', opts)}`
}

export function createEmptyDateRangeRow(): CourseDateRangeRow {
  return { id: crypto.randomUUID(), startDate: '', endDate: '' }
}

export function sortDateRangeRows(rows: CourseDateRangeRow[]): CourseDateRangeRow[] {
  return [...rows].sort((a, b) => {
    const aStart = a.startDate || ''
    const bStart = b.startDate || ''
    if (!aStart && !bStart) return 0
    if (!aStart) return 1
    if (!bStart) return -1
    if (aStart !== bStart) return aStart.localeCompare(bStart)
    const aEnd = a.endDate || ''
    const bEnd = b.endDate || ''
    if (!aEnd && !bEnd) return 0
    if (!aEnd) return 1
    if (!bEnd) return -1
    return aEnd.localeCompare(bEnd)
  })
}

export function normalizeDateRangeRows(rows: CourseDateRangeRow[]): CourseDateRange[] {
  return sortDateRangeRows(rows)
    .filter((r) => r.startDate && r.endDate)
    .map(({ startDate, endDate }) => ({ startDate, endDate }))
}

export function validateDateRanges(
  ranges: CourseDateRange[],
  today = todayISODate(),
): string | null {
  if (!ranges.length) return 'Add at least one course date range (start and end date)'
  for (let i = 0; i < ranges.length; i++) {
    const { startDate, endDate } = ranges[i]
    const label = `Date range ${i + 1}`
    if (!startDate || !endDate) return `${label}: start and end date are required`
    if (startDate < today) return `${label}: start date cannot be before today`
    if (endDate < startDate) return `${label}: end date must be on or after start date`
  }
  return null
}

export function parseDateRangesFromCourse(
  schedule?: {
    dateRanges?: CourseDateRange[]
    startDate?: string | Date
    endDate?: string | Date
    monthStartDays?: { month: number; year?: number; startDay: number }[]
    activeMonths?: number[]
    startDay?: number
  },
  courseDurationDays = 1,
): CourseDateRangeRow[] {
  if (schedule?.dateRanges?.length) {
    return schedule.dateRanges.map((r) => ({
      id: crypto.randomUUID(),
      startDate: String(r.startDate).slice(0, 10),
      endDate: String(r.endDate).slice(0, 10),
    }))
  }
  if (schedule?.startDate && schedule?.endDate) {
    return [{
      id: crypto.randomUUID(),
      startDate: String(schedule.startDate).slice(0, 10),
      endDate: String(schedule.endDate).slice(0, 10),
    }]
  }
  const entries = schedule?.monthStartDays ?? []
  if (entries.length) {
    const duration = Math.max(1, courseDurationDays)
    return entries.map((entry) => {
      const year = entry.year ?? new Date().getFullYear()
      const start = new Date(year, entry.month - 1, entry.startDay)
      const end = new Date(start)
      end.setDate(end.getDate() + duration - 1)
      return {
        id: crypto.randomUUID(),
        startDate: toISODateString(start),
        endDate: toISODateString(end),
      }
    })
  }
  return [createEmptyDateRangeRow()]
}

export function formatDateRangesSummary(ranges: CourseDateRange[]): string {
  if (!ranges.length) return 'No date ranges'
  return ranges.map((r) => formatDateRangeLabel(r.startDate, r.endDate)).join('; ')
}

const ISO_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/
const LEGACY_MONTH_RE = /^(\d{4})-(\d{2})$/

function parseBookingPeriodToken(token: string): CourseDateRange | null {
  if (token.includes('|')) {
    const [startDate, endDate] = token.split('|')
    if (
      startDate &&
      endDate &&
      ISO_DATE_ONLY_RE.test(startDate) &&
      ISO_DATE_ONLY_RE.test(endDate)
    ) {
      return { startDate, endDate }
    }
  }
  if (ISO_DATE_ONLY_RE.test(token)) {
    return { startDate: token, endDate: token }
  }
  return null
}

/** Display label for booking.selectedMonth (legacy YYYY-MM or start|end period token). */
export function formatBookingSelectedPeriod(
  selectedMonth: string | undefined,
  style: 'long' | 'short' = 'long',
): string {
  if (!selectedMonth) return ''

  const period = parseBookingPeriodToken(selectedMonth)
  if (period) {
    return formatDateRangeLabel(period.startDate, period.endDate)
  }

  const monthMatch = selectedMonth.match(LEGACY_MONTH_RE)
  const yearStr = monthMatch?.[1]
  const monthStr = monthMatch?.[2]
  if (yearStr && monthStr) {
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return new Date(year, month, 1).toLocaleDateString('en-US', {
      month: style === 'short' ? 'short' : 'long',
      year: 'numeric',
    })
  }

  return selectedMonth
}

export type CourseScheduleLike = {
  startDate?: string
  endDate?: string
  dateRanges?: CourseDateRange[]
  monthStartDays?: { month: number; year?: number; startDay: number }[]
  activeMonths?: number[]
  timeSlots?: unknown[]
}

export function hasCourseScheduleSection(schedule?: CourseScheduleLike): boolean {
  if (!schedule) return false
  return !!(
    (schedule.dateRanges && schedule.dateRanges.length > 0) ||
    schedule.startDate ||
    schedule.endDate ||
    (schedule.monthStartDays && schedule.monthStartDays.length > 0) ||
    (schedule.timeSlots && schedule.timeSlots.length > 0)
  )
}
